import { SCISSION, smooth, type Nucleus, type Vec3, seededRandom } from './ReactionSystem'
export const FRAGMENT_PROTONS = [56, 36] as const
export const FRAGMENT_MASSES = [141, 92] as const
export function packedPositions(count: number, seed = 31): Vec3[] {
  const random = seededRandom(seed)
  const points: Vec3[] = []
  for (let x = -7; x <= 7; x++) for (let y = -7; y <= 7; y++) for (let z = -7; z <= 7; z++) {
    if ((x + y + z) % 2 !== 0) continue
    points.push([x * .148 + (random() - .5) * .022, y * .148 + (random() - .5) * .022, z * .148 + (random() - .5) * .022])
  }
  return points.sort((a, b) => a.reduce((s, v) => s + v * v, 0) - b.reduce((s, v) => s + v * v, 0)).slice(0, count)
}
export function nucleonData(seed = 571) {
  const random = seededRandom(seed)
  const parent = packedPositions(236).sort((a, b) => a[0] - b[0])
  const ba = packedPositions(141, 43), kr = packedPositions(92, 71)
  const protons = new Uint8Array(236)
  for (const [offset, mass, charge] of [[0, 141, 56], [141, 92, 36]]) {
    const indices = Array.from({ length: mass }, (_, i) => offset + i)
    for (let i = indices.length - 1; i > 0; i--) { const j = Math.floor(random() * (i + 1)); [indices[i], indices[j]] = [indices[j], indices[i]] }
    for (let i = 0; i < charge; i++) protons[indices[i]] = 1
  }
  return { parent, ba, kr, protons }
}
export type NucleonData = ReturnType<typeof nucleonData>
/** A single continuous field: elongation, necking, lobe relaxation, then kinetic separation.
 * Instance identities persist through scission; no sphere/fragment replacement occurs.
 */
export function deformNucleon(out: Vec3, index: number, n: Pick<Nucleus, 'age'>, data: NucleonData) {
  const p = data.parent[index]
  const age = n.age
  if (age < .24) { out[0] = p[0]; out[1] = p[1]; out[2] = p[2]; return }
  const stretch = smooth((age - .24) / .65)
  const relax = smooth((age - .64) / (SCISSION - .64))
  const neck = 1 - .62 * stretch * Math.exp(-p[0] * p[0] / .13)
  const x = p[0] * (1 + .7 * stretch), y = p[1] * neck, z = p[2] * neck
  const heavy = index < 141
  const fragment = heavy ? data.ba[index] : data.kr[Math.min(91, index - 141)]
  const separationTime = Math.max(0, age - SCISSION)
  const distance = 1.6 + separationTime * .8 + separationTime * separationTime * 1.35
  const center = distance * (heavy ? -92 / 233 : 141 / 233)
  const spin = separationTime * (heavy ? .45 : -.65)
  const fy = fragment[1] * Math.cos(spin) - fragment[2] * Math.sin(spin)
  const fz = fragment[1] * Math.sin(spin) + fragment[2] * Math.cos(spin)
  out[0] = x + (fragment[0] + center - x) * relax
  out[1] = y + (fy - y) * relax
  out[2] = z + (fz - z) * relax
}
